import type { Casework } from "building-types/casework/caseworkTypes";
import type { ControllerPosition } from "building-types/electrical/controllerSelectionTypes";
import type { ElectricalLoad } from "building-types/electrical/electricalDeviceTypes";
import type { Load } from "building-types/electrical/generatedElectricalTypes";
import type { ReferenceGeom } from "building-types/entities/referenceGeom";
import type { Prop, PropAttributeTable } from "building-types/props/propTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import { RoomPositionType } from "building-types/rooms/roomPosition";
import type { Room } from "building-types/rooms/roomTypes";
import type { ElectricalItemCatalogSettings } from "building-types/settings/electricalItemCatalogSettings";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { Table } from "common/data/table";
import { assertIsNotNullOrUndefined } from "common/utils/assertOfType";
import type { WallLayerExtentsCache } from "doc/elements/openings/openingWallLayersTypes";
import { getLoadCatalog } from "doc/getLoadCatalog";
import type { MergedPropLibrary } from "doc/propTools";
import { vec2Copy } from "geom/vec2";
import { getRoomPositions } from "../../getRoomPositions";
import type { QueryPointAndNormal } from "../controllerSelection/controllersSelectionTypes";

export async function resolveLoadPosition(
  roomLayoutLoad: ElectricalLoad,
  room: Room,
  docRefGeom: Table<ReferenceGeom>,
  props: Table<Prop>,
  casework: Table<Casework>,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  electricalItemCatalogSettings: ElectricalItemCatalogSettings,
  mirror: boolean,
  roomOutlines: Table<RoomOutlines>,
  wallMasses: Table<WallMassSystem>,
  roomIndex: RoomIndex,
  wallLayerExtentsCache: WallLayerExtentsCache,
): Promise<Load[]> {
  const { loadType, position, hideConnections, id } = roomLayoutLoad;

  const loadCatalog = getLoadCatalog(electricalItemCatalogSettings);
  assertIsNotNullOrUndefined(loadType);

  const loadDescriptor = loadCatalog[loadType];
  const hostedOnReferenceGeom = position.type === RoomPositionType.REF_GEOM;

  const roomPositions = await getRoomPositions({
    roomPosition: position,
    room,
    docRefGeom,
    props,
    casework,
    propCatalog,
    mergedPropLibrary,
    mirror,
    roomOutlines,
    wallMasses,
    roomIndex,
    wallLayerExtentsCache,
  });

  const loadItems: Load[] = [];
  for (const p of roomPositions) {
    loadItems.push({
      loadType,
      loadDescriptor,
      position: vec2Copy(p.location),
      normal: vec2Copy(p.normal),
      levelId: room.level.id,
      sourceRoomHandle: room.roomHandle,
      hideConnections,
      hostedOnReferenceGeom,
      pickHandle: { type: "ELECTRICAL_DEVICE", id },
      handle: { type: "ELECTRICAL_DEVICE", id },
    });
  }
  return loadItems;
}

export async function resolveControllerPosition(
  pos: ControllerPosition,
  roomTable: Table<Room>,
  docRefGeom: Table<ReferenceGeom>,
  props: Table<Prop>,
  casework: Table<Casework>,
  propCatalog: PropAttributeTable,
  mergedPropLibrary: MergedPropLibrary,
  mirror: boolean,
  roomOutlines: Table<RoomOutlines>,
  wallMasses: Table<WallMassSystem>,
  roomIndex: RoomIndex,
  wallLayerExtentsCache: WallLayerExtentsCache,
): Promise<QueryPointAndNormal | null> {
  const { roomId, position } = pos;
  const room = roomTable[roomId];

  if (!room) {
    return null;
  }

  const {
    level: { id: levelId },
  } = room;

  const roomPositions = await getRoomPositions({
    roomPosition: position,
    room,
    docRefGeom,
    props,
    casework,
    propCatalog,
    mergedPropLibrary,
    mirror,
    roomOutlines,
    wallMasses,
    roomIndex,
    wallLayerExtentsCache,
  });

  if (roomPositions.length > 0) {
    return { levelId, point: vec2Copy(roomPositions[0].location), normal: vec2Copy(roomPositions[0].normal) };
  }

  return null;
}
